import React, { useState, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import './LoginPage.scss';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import company from '../../config/companyInfo';

const LOGO_URL = 'https://www.esaienergy.com/hubfs/Imported_Blog_Media/638777a841191e721b31c1f5_ESAI%20Logo_Black-p-500-1.png';
const LOGO_WHITE_URL = 'https://www.esaienergy.com/hubfs/Imported_Blog_Media/638777a841191e721b31c1f5_ESAI%20Logo_Black-p-500-1.png';
const LINKEDIN_URL = 'https://www.linkedin.com/company/esai/';
const TWITTER_URL = 'https://x.com/esaienergy';

function LoginPage() {
  const { instance } = useMsal();
  const [activeTab, setActiveTab] = useState('login');
  const [contact, setContact] = useState({ name: '', email: '', company: '', message: '' });
  const [requestType, setRequestType] = useState(null); // null = general contact, 'password-reset', 'account-request'
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitState, setSubmitState] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const honeypotRef = useRef(null);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  const handleTabChange = (tab, type = null) => {
    setActiveTab(tab);
    if (tab === 'contact') {
      setSubmitState('idle');
      setRequestType(type);
      setContact({ name: '', email: '', company: '', message: '' });
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitState('submitting');
    setErrorMessage('');
    const endpoint = requestType ? 'mondayForm' : 'contactForm';
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contact.name,
          email: contact.email,
          company: contact.company,
          message: contact.message,
          ...(requestType ? { requestType } : {}),
          turnstileToken,
          website: honeypotRef.current?.value ?? '',
        }),
      });
      if (res.ok) {
        setSubmitState('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
        setSubmitState('error');
      }
    } catch {
      setErrorMessage('Unable to send message. Please check your connection and try again.');
      setSubmitState('error');
    }
  };

  return (
    <div className="lp-root">
      {/* Header */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <img
            src={LOGO_WHITE_URL}
            alt="ESAI Energy"
            className="lp-header-logo"
          />
          <nav className="lp-header-nav">
            <a href="https://www.esaienergy.com" target="_blank" rel="noopener noreferrer" className="lp-header-link">
              esaienergy.com
            </a>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="lp-header-link lp-header-icon" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href={TWITTER_URL} target="_blank" rel="noopener noreferrer" className="lp-header-link lp-header-icon" aria-label="Twitter / X">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="lp-main">
        <div className="lp-card">
          {/* Card header with tabs */}
          <div className="lp-card-header">
            <div className="lp-card-logo-row">
              <img src={LOGO_URL} alt="ESAI Energy" className="lp-card-logo" />
              <span className="lp-card-title">ESAI Connect</span>
            </div>
            <div className="lp-tabs">
              <button
                className={`lp-tab${activeTab === 'contact' ? ' lp-tab--active' : ''}`}
                onClick={() => handleTabChange('contact')}
              >
                Contact
              </button>
              <button
                className={`lp-tab${activeTab === 'login' ? ' lp-tab--active' : ''}`}
                onClick={() => handleTabChange('login')}
              >
                Login
              </button>
            </div>
          </div>

          {/* Card body — two columns */}
          <div className="lp-card-body">
            {/* Left panel */}
            <div className="lp-left">
              {activeTab === 'login' && (
                <div className="lp-login-panel">
                  <h2 className="lp-panel-heading">Log In</h2>
                  <p className="lp-panel-desc">
                    Access the ESAI Connect platform with your email and password.
                  </p>
                  <button className="lp-login-btn" onClick={handleLogin}>
                    Sign In
                  </button>
                </div>
              )}

              {activeTab === 'contact' && (
                <div className="lp-contact-panel">
                  <h2 className="lp-panel-heading">
                    {requestType === 'password-reset' ? 'Password Reset' :
                     requestType === 'account-request' ? 'Request Access' :
                     'Contact Us'}
                  </h2>
                  {submitState === 'success' ? (
                    <div className="lp-contact-success">
                      <p>Thanks! We'll be in touch.</p>
                    </div>
                  ) : (
                    <form className="lp-contact-form" onSubmit={handleContactSubmit}>
                      {/* Honeypot — hidden from real users, bots fill it in */}
                      <input ref={honeypotRef} type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                      <label className="lp-label">
                        Name
                        <input
                          className="lp-input"
                          type="text"
                          value={contact.name}
                          onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
                          required
                          disabled={submitState === 'submitting'}
                        />
                      </label>
                      <label className="lp-label">
                        Email
                        <input
                          className="lp-input"
                          type="email"
                          value={contact.email}
                          onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                          required
                          disabled={submitState === 'submitting'}
                        />
                      </label>
                      <label className="lp-label">
                        Company
                        <input
                          className="lp-input"
                          type="text"
                          value={contact.company}
                          onChange={e => setContact(c => ({ ...c, company: e.target.value }))}
                          disabled={submitState === 'submitting'}
                        />
                      </label>
                      <label className="lp-label">
                        Message
                        <textarea
                          className="lp-input lp-textarea"
                          rows={4}
                          value={contact.message}
                          onChange={e => setContact(c => ({ ...c, message: e.target.value }))}
                          required
                          disabled={submitState === 'submitting'}
                        />
                      </label>
                      <Turnstile
                        siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                        onSuccess={token => setTurnstileToken(token)}
                        onExpire={() => setTurnstileToken('')}
                        onError={() => setTurnstileToken('')}
                        options={{ theme: 'light' }}
                      />
                      {submitState === 'error' && (
                        <p className="lp-contact-error">{errorMessage}</p>
                      )}
                      <button
                        type="submit"
                        className="lp-login-btn"
                        disabled={submitState === 'submitting' || !turnstileToken}
                      >
                        {submitState === 'submitting' ? 'Sending…' : 'Send Message'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="lp-divider" />

            {/* Right panel */}
            <div className="lp-right">
              <div className="lp-info-box">
                <h3 className="lp-info-box-heading">Forgot your password?</h3>
                <p className="lp-info-box-text">
                  Click Sign In and select "Forgot my password" to reset it via email.
                </p>
              </div>

              <div className="lp-info-box">
                <h3 className="lp-info-box-heading">New to ESAI Connect?</h3>
                <p className="lp-info-box-text">
                  ESAI Connect provides clients with access to ESAI's global energy market outlook data, forecasts, and publications. To request access, contact us.
                </p>
                <button className="lp-info-btn" onClick={() => handleTabChange('contact', 'account-request')}>
                  Contact Us
                </button>
              </div>
            </div>
          </div>

          {/* Card footer */}
          <div className="lp-card-footer">
            <span>{company.name}</span>
            <span className="lp-footer-sep">|</span>
            <span>{company.address}</span>
            <span className="lp-footer-sep">|</span>
            <span>Phone: {company.phone}</span>
            <span className="lp-footer-sep">|</span>
            <a href={company.links.privacyPolicy.url} target={company.links.privacyPolicy.target} rel="noopener noreferrer" className="lp-footer-policy-link">{company.links.privacyPolicy.label}</a>
            <span className="lp-footer-sep">|</span>
            <a href={company.links.cyberSecurity.url} target={company.links.cyberSecurity.target} rel="noopener noreferrer" className="lp-footer-policy-link">{company.links.cyberSecurity.label}</a>
          </div>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
